import type { AuthTokens } from '@neosocial/protocol';
import { storage } from '../storage';
import type { TokenStore } from './client';

/** Where the refresh token is kept (`ns:refresh`): present while a session is saved here. */
export const REFRESH_KEY = 'refresh';

/**
 * The access token lives in memory only; the rotating refresh token is
 * kept so a reload stays signed in (user-management §2.2: no "keep me
 * signed in" box; the web has a shorter idle limit on the server).
 */
export function createTokenStore(now: () => number = Date.now): TokenStore {
  let access: string | null = null;
  let expiresAt: number | null = null;
  return {
    access: () => access,
    accessExpiresAt: () => expiresAt,
    refresh: () => storage.get(REFRESH_KEY),
    save(t: AuthTokens) {
      access = t.access_token;
      expiresAt = now() + t.expires_in * 1000;
      storage.set(REFRESH_KEY, t.refresh_token);
    },
    clear() {
      access = null;
      expiresAt = null;
      storage.remove(REFRESH_KEY);
    },
  };
}

/** Reads a JWT's payload without checking it (the server and comms-voice check it). */
export function decodeJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(pad);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    return null;
  }
}
